import { memo, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "components/icons";
import { handleAxiosError } from "app/services/app.error.handler";
import { parseApiError } from "app/services/api.error.parser";
import MDBox from "components/MDBox";
import MasterDataSelect from "app/components/shared/MasterDataSelect";
import { useDispatch, useSelector } from "react-redux";
import { isClientMissing, validateMemberReportingObject } from "utils/userValidationUtil";
import {
  loadMembersForAllClientsFromBackend,
  addMemberReportingToBackend,
  updateMemberReportingToBackend,
} from "app/services/member.service";
import { managerActions } from "store/manager-slice";
import { loadAllManagersFromBackend } from "app/services/manager.service";
import { memberActions } from "store/member-slice";
import { loadAllClients } from "app/services/client.service";
import { clientActions } from "store/client-slice";

const obj = {
  clientId: null,
  managerId: null,
  memberId: null,
};

// A failed load leaves any cached list in place, so it only needs to reach the user
// when there is nothing cached to fall back on. Session expiry always surfaces.
const reportLoadFailure = (error, hasCachedData) => {
  if (!hasCachedData || parseApiError(error).isUnauthorized) {
    handleAxiosError(error);
    return;
  }
  console.error("Keeping cached list; refresh failed:", parseApiError(error).message);
};

const AddMemberDialog = ({ open, setOpen, refreshActivitiesDataHandler, editObject }) => {
  const dispatch = useDispatch();
  const [message, setMessage] = useState("");
  const [messageOpen, setMessageOpen] = useState(false);
  const [severity, setSeverity] = useState("info"); // "error" or "warning" or "info" or "success"
  const [data, setData] = useState(obj);
  const clients = useSelector((state) => state.client.rows);
  const managers = useSelector((state) => state.manager.rows);
  const members = useSelector((state) => state.member.rows);

  // Mirrors the lists so the load effect can read them without taking them as
  // dependencies: it must fire when the dialog opens, not when a list changes.
  const cachedListsRef = useRef({ clients, members, managers });
  useEffect(() => {
    cachedListsRef.current = { clients, members, managers };
  });

  // Only the newest open may write to the store, so a slow response from an
  // earlier open cannot land on top of fresher data.
  const loadSeqRef = useRef(0);

  useEffect(() => {
    if (!open) return;
    const cached = cachedListsRef.current;
    const seq = ++loadSeqRef.current;

    const replaceWith = (action) => (response) => {
      if (seq !== loadSeqRef.current) return;
      dispatch(action(Array.isArray(response.data) ? response.data : []));
    };
    const reportFailure = (hasCachedData) => (error) => {
      if (seq !== loadSeqRef.current) return;
      reportLoadFailure(error, hasCachedData);
    };

    if (cached.clients.length === 0) {
      loadAllClients()
        .then(replaceWith(clientActions.replaceData))
        .catch(reportFailure(false));
    }
    // Members and managers are refetched on every open (not just when empty) so ones
    // added since the page loaded appear here without a full browser refresh.
    loadMembersForAllClientsFromBackend()
      .then(replaceWith(memberActions.replaceData))
      .catch(reportFailure(cached.members.length > 0));
    loadAllManagersFromBackend()
      .then(replaceWith(managerActions.replaceData))
      .catch(reportFailure(cached.managers.length > 0));
  }, [open, dispatch]);

  useEffect(() => {
    setMessageOpen(false);
    setMessage("");
    if (!open) return;
    if (editObject) {
      if (isClientMissing(clients, editObject.client?.id)) {
        setData(obj);
        setErrorAction("The client for this reporting no longer exists. Please delete this reporting record.");
        return;
      }
      setData({
        clientId: parseInt(editObject.client.id, 10),
        managerId: parseInt(editObject.managerId, 10),
        memberId: parseInt(editObject.member.id, 10),
      });
    } else {
      setData(obj);
    }
  }, [open, editObject, clients]);

  const saveMemberInBackend = async () => {
    if (!validateMemberReportingObject(data)) {
      setErrorAction(
        "One or more required fields for a Member are missing or having invalid value"
      );
      return;
    }
    const payload = { memberId: data.memberId, managerId: data.managerId };
    await (editObject ? updateMemberInvoker(payload) : addMemberInvoker(payload));
  };

  const addMemberInvoker = async (userObject) => {
    await addMemberReportingToBackend(userObject)
      .then((data) => {
        setSuccessAction("Member added successfully");
        setTimeout(() => {
          setData(obj);
          setMessageOpen(false);
          setMessage("");
          setOpen(false);
          refreshActivitiesDataHandler();
        }, 1000);
      })
      .catch((error) => {
        handleAxiosError(error);
      });
  };

  const updateMemberInvoker = async (payload) => {
    await updateMemberReportingToBackend(payload.memberId, payload)
      .then((data) => {
        setSuccessAction("Member Updated successfully");
        setTimeout(() => {
          setData(obj);
          setMessageOpen(false);
          setMessage("");
          setOpen(false);
          refreshActivitiesDataHandler();
        }, 1000);
      })
      .catch((error) => {
        handleAxiosError(error);
      });
  };

  const setErrorAction = (errorMessage) => {
    setMessage(errorMessage);
    setMessageOpen(true);
    setSeverity("error");
  };

  const setSuccessAction = (message) => {
    setMessage(message);
    setMessageOpen(true);
    setSeverity("success");
  };

  const isEditing = !!editObject;
  const clientChosen = !!data.clientId;

  const membersForClient = useMemo(
    () =>
      members.filter(
        (member) =>
          member.client != null &&
          member.client.id != null &&
          parseInt(member.client.id, 10) === data.clientId
      ),
    [members, data.clientId]
  );

  const managersForClient = useMemo(
    () =>
      managers.filter(
        (manager) =>
          manager.clientId != null && parseInt(manager.clientId, 10) === data.clientId
      ),
    [managers, data.clientId]
  );

  return (
    <Dialog open={open} fullWidth={true} maxWidth="sm">
      <DialogTitle>{editObject ? `Update Member Reporting` : `Add New Reporting`}</DialogTitle>
      <DialogContent>
        <MDBox my={2}>
          <MasterDataSelect
            id="client"
            label="Client"
            placeholder="Select a client"
            options={clients}
            value={data?.clientId ?? ""}
            onChange={(e) =>
              setData({
                clientId: e.target.value !== "" ? parseInt(e.target.value, 10) : null,
                memberId: null,
                managerId: null,
              })
            }
            disabled={isEditing}
            helperText={isEditing ? undefined : "*Required"}
          />
        </MDBox>
        <MDBox my={2}>
          <MasterDataSelect
            id="member"
            label="Member"
            placeholder="Select a member"
            options={membersForClient}
            value={data?.memberId ?? ""}
            onChange={(e) =>
              setData({
                ...data,
                memberId: e.target.value !== "" ? parseInt(e.target.value, 10) : null,
              })
            }
            disabled={isEditing || !clientChosen}
            helperText={isEditing ? undefined : "*Required"}
          />
        </MDBox>
        <MDBox my={2}>
          <MasterDataSelect
            id="manager"
            label="Manager"
            placeholder="Select a manager"
            options={managersForClient}
            getOptionLabel={(manager) => `${manager.name} ${manager.clientEmail}`}
            value={data?.managerId ?? ""}
            onChange={(e) =>
              setData({
                ...data,
                managerId: e.target.value !== "" ? parseInt(e.target.value, 10) : null,
              })
            }
            disabled={!clientChosen}
            helperText="*Required"
          />
        </MDBox>
        <Collapse in={messageOpen}>
          <Alert
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setMessageOpen(false);
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            severity={severity}
            sx={{ mb: 2 }}
          >
            {message}
          </Alert>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>Cancel</Button>
        <Button onClick={saveMemberInBackend} variant="contained">{editObject ? `Update` : `Add`}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(AddMemberDialog);
